import React, { ReactNode, useContext } from 'react';
import type { PageProps } from 'gatsby';
import { PageSettingsDataModel } from '@brz-types/common/page.settings.type';
import { INITIAL_HEADER_HEIGHT } from '@brz-components/header/header';
import { Backdrop, CircularProgress } from '@mui/material';
import { useClass } from '@breezerr';



export type PagePropsModel = {
    location: PageProps['location'],
    params: PageProps['params']
}
interface PageContextModel {
    page: PageSettingsDataModel;
    template: TemplateSettingsProps;
    pageProps?: PagePropsModel;
}

export type TemplateSettingsProps = {
    disable?: {
        faqSchema?: boolean;
        howToSchema?: boolean;
        orgSchema?: boolean;
        //
        footerContent?: boolean;
        navigation?: boolean;
    }
};

type PageContextProps = {
    pageSettings: PageSettingsDataModel;
    templateSettings: TemplateSettingsProps;
    head?: boolean;
    children: ReactNode | ReactNode [];
    pageProps?: PagePropsModel;
};

const PageContext = React.createContext<PageContextModel>({} as PageContextModel);

/**
 * A Page wrapper that takes the page data and makes it available to all the children via usePage() hook.
 * It also set's up the SEO related tags in the <head> of the page
 * @param props 
 * + "pageSettings" is the page data passed through useTina()
 * + "templateSettings" settings to pass through from a page template to configure any child consumer component 
 * + "children" any ReactNode (Page Layout component for example)
 * @returns A React ContextProvider component
 */
export const PageContextProvider = (props: PageContextProps) => {
    const { pageSettings, templateSettings, children, pageProps } = props;

    const value: PageContextModel = {
        page: {
            ...pageSettings,
            _computed: {
                topHeight: React.useCallback((offset: number = 0) => {
                    return pageSettings?.notification ? INITIAL_HEADER_HEIGHT + 45 + offset : INITIAL_HEADER_HEIGHT + offset
                }, [pageSettings?.notification])
            }
        },
        template: templateSettings,
        pageProps: pageProps
    };    

    return (
        <PageContext.Provider value={value}>
            { children }
        </PageContext.Provider>
    );
};

// Hook
export const usePage = () => useContext(PageContext);


PageContextProvider.defaultProps = {
    head: false,
    templateSettings: {
        disable: {
            howToSchema: false,
            faqSchema: false,
            orgSchema: false,
        }
    }
}